import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { Edit as EditIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import useModules from "../../hooks/useModules";
import useCompanies from "../../hooks/useCompanies";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    overflowX: "scroll",
    ...theme.scrollbarStyles,
  },
}));

export function AiPackageForm({ onSubmit, onDelete, onCancel, initialValue, loading }) {
  const classes = useStyles();
  const [record, setRecord] = useState({
    name: "",
    tokens: 0,
    amount: 0,
    isActive: true,
  });

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  return (
    <Formik
      enableReinitialize
      initialValues={record}
      onSubmit={(values, { resetForm }) =>
        setTimeout(() => {
          onSubmit(values);
          resetForm();
        }, 300)
      }
    >
      {() => (
        <Form className={classes.fullWidth}>
          <Grid spacing={1} container>
            <Grid xs={12} sm={6} md={3} item>
              <Field
                as={TextField}
                label={i18n.t("aiTokens.packages.form.name")}
                name="name"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
              />
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <Field
                as={TextField}
                label={i18n.t("aiTokens.packages.form.tokens")}
                name="tokens"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
              />
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <Field
                as={TextField}
                label={i18n.t("aiTokens.packages.form.price")}
                name="amount"
                variant="outlined"
                className={classes.fullWidth}
                margin="dense"
                type="number"
              />
            </Grid>
            <Grid xs={12} sm={6} md={2} item>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel htmlFor="active-selection">{i18n.t("aiTokens.packages.form.active")}</InputLabel>
                <Field
                  as={Select}
                  id="active-selection"
                  label={i18n.t("aiTokens.packages.form.active")}
                  name="isActive"
                  margin="dense"
                >
                  <MenuItem value={true}>{i18n.t("common.yes")}</MenuItem>
                  <MenuItem value={false}>{i18n.t("common.no")}</MenuItem>
                </Field>
              </FormControl>
            </Grid>
          </Grid>

          <Grid spacing={2} justifyContent="flex-end" container>
            <Grid sm={3} md={2} item>
              <ButtonWithSpinner
                className={classes.fullWidth}
                loading={loading}
                onClick={() => onCancel()}
                variant="contained"
              >
                {i18n.t("aiTokens.packages.buttons.clear")}
              </ButtonWithSpinner>
            </Grid>
            {record.id !== undefined ? (
              <Grid sm={3} md={2} item>
                <ButtonWithSpinner
                  className={classes.fullWidth}
                  loading={loading}
                  onClick={() => onDelete(record)}
                  variant="contained"
                  color="secondary"
                >
                  {i18n.t("aiTokens.packages.buttons.deactivate")}
                </ButtonWithSpinner>
              </Grid>
            ) : null}
            <Grid sm={3} md={2} item>
              <ButtonWithSpinner
                className={classes.fullWidth}
                loading={loading}
                type="submit"
                variant="contained"
                color="primary"
              >
                {i18n.t("aiTokens.packages.buttons.save")}
              </ButtonWithSpinner>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

export function AiPackagesGrid({ records, onSelect }) {
  const classes = useStyles();
  return (
    <Paper className={classes.tableContainer}>
      <Table className={classes.fullWidth} padding="none">
        <TableHead>
          <TableRow>
            <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
            <TableCell>{i18n.t("aiTokens.packages.table.name")}</TableCell>
            <TableCell align="center">{i18n.t("aiTokens.packages.table.tokens")}</TableCell>
            <TableCell align="center">{i18n.t("aiTokens.packages.table.price")}</TableCell>
            <TableCell align="center">{i18n.t("aiTokens.packages.table.active")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row) => (
            <TableRow key={row.id}>
              <TableCell align="center" style={{ width: "1%" }}>
                <IconButton onClick={() => onSelect(row)}>
                  <EditIcon />
                </IconButton>
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell align="center">{row.tokens}</TableCell>
              <TableCell align="center">{Number(row.amount || 0).toFixed(2)}</TableCell>
              <TableCell align="center">{row.is_active ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

const AiPackagesManager = () => {
  const classes = useStyles();
  const {
    listAiPackages,
    saveAiPackage,
    updateAiPackage,
    removeAiPackage,
    addCompanyAiCredits,
  } = useModules();
  const { list: listCompanies } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: "",
    tokens: 0,
    amount: 0,
    isActive: true,
  });

  const [companies, setCompanies] = useState([]);
  const [creditCompanyId, setCreditCompanyId] = useState("");
  const [creditPackageId, setCreditPackageId] = useState("");

  useEffect(() => {
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  useEffect(() => {
    (async () => {
      try {
        const list = await listCompanies();
        setCompanies(list);
      } catch (e) {
        toast.error(i18n.t("aiTokens.packages.toasts.companiesLoadError"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const packages = await listAiPackages({ active_only: false });
      setRecords(packages);
    } catch (e) {
      toast.error(i18n.t("aiTokens.packages.toasts.packagesLoadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await updateAiPackage(data);
      } else {
        await saveAiPackage(data);
      }
      await loadPackages();
      handleCancel();
      toast.success(i18n.t("aiTokens.packages.toasts.packageSaved"));
    } catch (e) {
      toast.error(i18n.t("aiTokens.packages.toasts.packageSaveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await removeAiPackage(record.id);
      await loadPackages();
      handleCancel();
      toast.success(i18n.t("aiTokens.packages.toasts.packageDeactivated"));
    } catch (e) {
      toast.error(i18n.t("aiTokens.packages.toasts.packageDeactivateError"));
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setRecord({
      id: undefined,
      name: "",
      tokens: 0,
      amount: 0,
      isActive: true,
    });
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      name: data.name || "",
      tokens: data.tokens || 0,
      amount: data.amount || 0,
      isActive: data.is_active !== false,
    });
  };

  const handleCredit = async () => {
    if (!creditCompanyId || !creditPackageId) {
      toast.error(i18n.t("aiTokens.packages.toasts.selectCompanyAndPackage"));
      return;
    }
    setLoading(true);
    try {
      await addCompanyAiCredits(creditCompanyId, { packageId: creditPackageId });
      toast.success(i18n.t("aiTokens.packages.toasts.creditsAssigned"));
    } catch (e) {
      toast.error(i18n.t("aiTokens.packages.toasts.creditsAssignError"));
    }
    setLoading(false);
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <AiPackageForm
            initialValue={record}
            onDelete={() => setShowConfirmDialog(true)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid xs={12} item>
          <AiPackagesGrid records={records} onSelect={handleSelect} />
        </Grid>
        <Grid xs={12} item>
          <Paper style={{ padding: 16 }}>
            <Grid spacing={2} container alignItems="center">
              <Grid xs={12} md={4} item>
                <FormControl variant="outlined" fullWidth margin="dense">
                  <InputLabel>{i18n.t("aiTokens.packages.credit.company")}</InputLabel>
                  <Select
                    value={creditCompanyId}
                    onChange={(e) => setCreditCompanyId(e.target.value)}
                    label={i18n.t("aiTokens.packages.credit.company")}
                  >
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} md={4} item>
                <FormControl variant="outlined" fullWidth margin="dense">
                  <InputLabel>{i18n.t("aiTokens.packages.credit.package")}</InputLabel>
                  <Select
                    value={creditPackageId}
                    onChange={(e) => setCreditPackageId(e.target.value)}
                    label={i18n.t("aiTokens.packages.credit.package")}
                  >
                    {records.filter((p) => p.is_active).map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} ({p.tokens})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} md={4} item>
                <ButtonWithSpinner
                  variant="contained"
                  color="primary"
                  onClick={handleCredit}
                  loading={loading}
                >
                  {i18n.t("aiTokens.packages.buttons.assignCredits")}
                </ButtonWithSpinner>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <ConfirmationModal
        title={i18n.t("aiTokens.packages.confirm.deactivateTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("aiTokens.packages.confirm.deactivateMessage")}
      </ConfirmationModal>
    </Paper>
  );
};

export default AiPackagesManager;
